import { apiClient } from '@/shared/services/client'
import type {
  CampaniasPaginadas,
  CampaniaDetail,
  CampaniaCreatePayload,
  CampaniaUpdatePayload,
  SolicitudesPaginadas,
  SolicitudDetail,
  FiltrosSolicitudes,
  ActualizarEstadoPayload,
  ActualizarSeguimientoPayload,
  FormularioCompleto,
  PasoCreatePayload,
  CampoCreatePayload,
  PasoFormulario,
  CampoFormulario,
  EncuestaConfig,
  EncuestaAnaliticas,
  UsuarioTonal,
  SuccessResponse,
} from '../types'

const BASE = '/muestras-medicas'

// === CAMPAÑAS ===

export async function getCampanias(params?: {
  page?: number
  page_size?: number
  search?: string
  grupo_laboratorio_id?: number
}): Promise<CampaniasPaginadas> {
  const { data } = await apiClient.get(`${BASE}/campanias/`, { params })
  return data
}

export async function getCampania(id: number): Promise<CampaniaDetail> {
  const { data } = await apiClient.get(`${BASE}/campanias/${id}/`)
  return data
}

export async function crearCampania(payload: CampaniaCreatePayload): Promise<CampaniaDetail> {
  const { data } = await apiClient.post(`${BASE}/campanias/`, payload)
  return data
}

export async function actualizarCampania(id: number, payload: CampaniaUpdatePayload): Promise<CampaniaDetail> {
  const { data } = await apiClient.put(`${BASE}/campanias/${id}/`, payload)
  return data
}

export async function eliminarCampania(id: number): Promise<SuccessResponse> {
  const { data } = await apiClient.delete(`${BASE}/campanias/${id}/`)
  return data
}

export async function toggleCampania(id: number): Promise<SuccessResponse> {
  const { data } = await apiClient.post(`${BASE}/campanias/${id}/toggle/`)
  return data
}

export async function descargarQR(id: number): Promise<Blob> {
  const { data } = await apiClient.get(`${BASE}/campanias/${id}/qr/`, {
    responseType: 'blob',
  })
  return data
}

export async function getUsuariosTonal(): Promise<UsuarioTonal[]> {
  const { data } = await apiClient.get(`${BASE}/campanias/usuarios-tonal/`)
  return data
}

// === SOLICITUDES ===

export async function getSolicitudes(
  campaniaId: number,
  filtros?: FiltrosSolicitudes,
): Promise<SolicitudesPaginadas> {
  const { data } = await apiClient.get(`${BASE}/solicitudes/${campaniaId}/`, {
    params: filtros,
  })
  return data
}

export async function getSolicitudDetalle(solicitudId: number): Promise<SolicitudDetail> {
  const { data } = await apiClient.get(`${BASE}/solicitudes/detalle/${solicitudId}/`)
  return data
}

export async function actualizarEstado(payload: ActualizarEstadoPayload): Promise<SuccessResponse> {
  const { data } = await apiClient.patch(`${BASE}/solicitudes/actualizar-estado/`, payload)
  return data
}

export async function actualizarSeguimiento(payload: ActualizarSeguimientoPayload): Promise<SuccessResponse> {
  const { data } = await apiClient.patch(`${BASE}/solicitudes/actualizar-seguimiento/`, payload)
  return data
}

export async function exportarSolicitudes(campaniaId: number): Promise<Blob> {
  const { data } = await apiClient.get(`${BASE}/solicitudes/${campaniaId}/exportar/`, {
    responseType: 'blob',
  })
  return data
}

export async function exportarGuiaEnvio(campaniaId: number, fecha?: string): Promise<Blob> {
  const { data } = await apiClient.get(`${BASE}/solicitudes/${campaniaId}/exportar-guia/`, {
    params: fecha ? { fecha } : undefined,
    responseType: 'blob',
  })
  return data
}

// === FORM BUILDER ===

export async function getFormulario(campaniaId: number): Promise<FormularioCompleto> {
  const { data } = await apiClient.get(`${BASE}/form-builder/${campaniaId}/`)
  return data
}

export async function crearPaso(campaniaId: number, payload: PasoCreatePayload): Promise<PasoFormulario> {
  const { data } = await apiClient.post(`${BASE}/form-builder/${campaniaId}/pasos/`, payload)
  return data
}

export async function editarPaso(campaniaId: number, pasoId: number, payload: Partial<PasoCreatePayload>): Promise<PasoFormulario> {
  const { data } = await apiClient.put(`${BASE}/form-builder/${campaniaId}/pasos/${pasoId}/`, payload)
  return data
}

export async function eliminarPaso(campaniaId: number, pasoId: number): Promise<SuccessResponse> {
  const { data } = await apiClient.delete(`${BASE}/form-builder/${campaniaId}/pasos/${pasoId}/`)
  return data
}

export async function reordenarPasos(campaniaId: number, orden: number[]): Promise<SuccessResponse> {
  const { data } = await apiClient.post(`${BASE}/form-builder/${campaniaId}/pasos/reordenar/`, { orden })
  return data
}

export async function crearCampo(campaniaId: number, payload: CampoCreatePayload): Promise<CampoFormulario> {
  const { data } = await apiClient.post(`${BASE}/form-builder/${campaniaId}/campos/`, payload)
  return data
}

export async function getCampo(campaniaId: number, campoId: number): Promise<CampoFormulario> {
  const { data } = await apiClient.get(`${BASE}/form-builder/${campaniaId}/campos/${campoId}/`)
  return data
}

export async function editarCampo(campaniaId: number, campoId: number, payload: Partial<CampoCreatePayload>): Promise<CampoFormulario> {
  const { data } = await apiClient.put(`${BASE}/form-builder/${campaniaId}/campos/${campoId}/`, payload)
  return data
}

export async function eliminarCampo(campaniaId: number, campoId: number): Promise<SuccessResponse> {
  const { data } = await apiClient.delete(`${BASE}/form-builder/${campaniaId}/campos/${campoId}/`)
  return data
}

export async function reordenarCampos(campaniaId: number, orden: number[]): Promise<SuccessResponse> {
  const { data } = await apiClient.post(`${BASE}/form-builder/${campaniaId}/campos/reordenar/`, { orden })
  return data
}

// === ENCUESTAS ===

export async function getEncuesta(campaniaId: number): Promise<EncuestaConfig> {
  const { data } = await apiClient.get(`${BASE}/encuestas/${campaniaId}/`)
  return data
}

export async function toggleMultiplesRespuestas(campaniaId: number): Promise<SuccessResponse> {
  const { data } = await apiClient.post(`${BASE}/encuestas/${campaniaId}/toggle-multiples/`)
  return data
}

export async function getFormularioEncuesta(campaniaId: number): Promise<FormularioCompleto> {
  const { data } = await apiClient.get(`${BASE}/encuestas/${campaniaId}/form-builder/`)
  return data
}

export async function crearPasoEncuesta(campaniaId: number, payload: PasoCreatePayload) {
  const { data } = await apiClient.post(`${BASE}/encuestas/${campaniaId}/pasos/`, payload)
  return data
}

export async function editarPasoEncuesta(campaniaId: number, pasoId: number, payload: Partial<PasoCreatePayload>) {
  const { data } = await apiClient.put(`${BASE}/encuestas/${campaniaId}/pasos/${pasoId}/`, payload)
  return data
}

export async function eliminarPasoEncuesta(campaniaId: number, pasoId: number) {
  const { data } = await apiClient.delete(`${BASE}/encuestas/${campaniaId}/pasos/${pasoId}/`)
  return data
}

export async function reordenarPasosEncuesta(campaniaId: number, orden: number[]) {
  const { data } = await apiClient.post(`${BASE}/encuestas/${campaniaId}/pasos/reordenar/`, { orden })
  return data
}

export async function crearCampoEncuesta(campaniaId: number, payload: CampoCreatePayload) {
  const { data } = await apiClient.post(`${BASE}/encuestas/${campaniaId}/campos/`, payload)
  return data
}

export async function obtenerCampoEncuesta(campaniaId: number, campoId: number) {
  const { data } = await apiClient.get(`${BASE}/encuestas/${campaniaId}/campos/${campoId}/`)
  return data
}

export async function editarCampoEncuesta(campaniaId: number, campoId: number, payload: Partial<CampoCreatePayload>) {
  const { data } = await apiClient.put(`${BASE}/encuestas/${campaniaId}/campos/${campoId}/`, payload)
  return data
}

export async function eliminarCampoEncuesta(campaniaId: number, campoId: number) {
  const { data } = await apiClient.delete(`${BASE}/encuestas/${campaniaId}/campos/${campoId}/`)
  return data
}

export async function reordenarCamposEncuesta(campaniaId: number, orden: number[]) {
  const { data } = await apiClient.post(`${BASE}/encuestas/${campaniaId}/campos/reordenar/`, { orden })
  return data
}

export async function getAnaliticasEncuesta(campaniaId: number): Promise<EncuestaAnaliticas> {
  const { data } = await apiClient.get(`${BASE}/encuestas/${campaniaId}/analiticas/`)
  return data
}

export async function exportarRespuestasEncuesta(campaniaId: number): Promise<Blob> {
  const { data } = await apiClient.get(`${BASE}/encuestas/${campaniaId}/exportar/`, {
    responseType: 'blob',
  })
  return data
}
